import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import LogoMark from './LogoMark';
import { NAV_LINKS } from '../siteInfo';
import { LANGUAGES, UI, localizePath, rememberLanguage, switchLanguagePath, useLang } from '../i18n';

const desktopLinkClass = ({ isActive }) =>
  `px-2 xl:px-3 py-2 rounded-md font-medium transition-colors ${isActive
    ? 'text-blue-700 bg-blue-50 hover:text-blue-700'
    : 'text-gray-700 hover:text-blue-700 hover:bg-gray-100'
  }`;

const mobileLinkClass = ({ isActive }) =>
  `block px-4 py-3 rounded-md text-lg font-medium ${isActive
    ? 'text-blue-700 bg-blue-50 hover:text-blue-700'
    : 'text-gray-800 hover:text-blue-700 hover:bg-gray-100'
  }`;

const LanguageSwitch = ({ className, long }) => {
  const { pathname, hash } = useLocation();
  const lang = useLang();
  return (
    <div className={`flex items-center rounded-lg border border-gray-300 overflow-hidden text-sm font-semibold ${className}`} role="group" aria-label={UI[lang].language}>
      {LANGUAGES.map(({ code, label, name }) => (
        <Link
          key={code}
          to={switchLanguagePath(pathname, code) + hash}
          onClick={() => rememberLanguage(code)}
          lang={code}
          hrefLang={code}
          aria-current={code === lang ? 'true' : undefined}
          className={`px-2.5 lg:px-2 xl:px-2.5 py-1.5 transition-colors ${code === lang
            ? 'bg-gray-900 text-white hover:text-white'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          {long ? name : label}
        </Link>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const lang = useLang();
  const t = UI[lang];

  // Close the mobile menu after navigating
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <header className="bg-white/95 backdrop-blur shadow-sm">
      <nav className="container mx-auto px-4 h-16 flex items-center justify-between" aria-label={t.mainMenu}>
        <Link to={localizePath('/', lang)} className="flex items-center gap-3 text-gray-900 hover:text-gray-900">
          <LogoMark className="h-11 w-11" />
          <span className="text-lg md:text-xl font-bold tracking-wide">AKKA DEMİR ÇELİK</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map(({ to, key }) => (
            <NavLink key={to} to={localizePath(to, lang)} end={to === '/'} className={desktopLinkClass}>
              {t.nav[key]}
            </NavLink>
          ))}
          <LanguageSwitch className="ml-2 xl:ml-3" />
          <Link
            to={localizePath('/contact', lang)}
            className="ml-2 xl:ml-3 px-4 xl:px-5 py-2 rounded-lg bg-blue-600 whitespace-nowrap text-white font-semibold hover:bg-blue-700 hover:text-white transition-colors"
          >
            {t.quote}
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 rounded-md text-gray-800 hover:bg-gray-100"
          aria-controls="mobile-menu"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? t.closeMenu : t.openMenu}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            {menuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </nav>

      {menuOpen && (
        <div id="mobile-menu" className="lg:hidden border-t border-gray-200 bg-white">
          <div className="container mx-auto px-4 py-3 space-y-1">
            {NAV_LINKS.map(({ to, key }) => (
              <NavLink key={to} to={localizePath(to, lang)} end={to === '/'} className={mobileLinkClass}>
                {t.nav[key]}
              </NavLink>
            ))}
            <LanguageSwitch className="w-fit mx-4 my-3" long />
            <Link
              to={localizePath('/contact', lang)}
              className="block mt-2 px-4 py-3 rounded-lg bg-blue-600 text-white text-lg font-semibold text-center hover:bg-blue-700 hover:text-white"
            >
              {t.quote}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
